<template>
  <section class="profile-page">
    <nav class="profile-crumb">个人中心 / <b>账号基本信息</b></nav>
    <page-header title="账号基本信息" description="查看账号资料，维护个人联系方式和登录密码。" />

    <div class="profile-layout">
      <div class="panel profile-card">
        <div class="profile-heading"><div><h2>个人资料</h2><p>更新后将在平台中显示最新信息。</p></div><span class="profile-role">{{ roleName }}</span></div>
        <div v-if="profileError" class="notice error" role="alert">{{ profileError }}</div>
        <div v-if="profileSuccess" class="notice success" role="status">{{ profileSuccess }}</div>
        <form @submit.prevent="saveProfile" @input="profileError = ''; profileSuccess = ''">
          <div class="form-grid profile-fields">
            <div class="form-item"><label for="profile-account">登录账号</label><input id="profile-account" :value="user.account || '—'" class="field full" readonly></div>
            <div class="form-item"><label for="profile-role">账号角色</label><input id="profile-role" :value="roleName" class="field full" readonly></div>
            <div v-if="user.role === 'STUDENT'" class="form-item"><label for="profile-student-no">学号</label><input id="profile-student-no" :value="user.studentNo || '—'" class="field full" readonly></div>
            <div v-if="user.role === 'STUDENT'" class="form-item"><label for="profile-class">所属班级</label><input id="profile-class" :value="user.className || '—'" class="field full" readonly></div>
            <div class="form-item"><label for="profile-name">姓名 <span class="profile-required">*</span></label><input id="profile-name" v-model.trim="form.name" class="field full" maxlength="50" required autocomplete="name"></div>
            <div class="form-item"><label for="profile-phone">联系电话</label><input id="profile-phone" v-model.trim="form.phone" class="field full" type="tel" maxlength="30" autocomplete="tel" placeholder="请输入联系电话"></div>
            <div class="form-item"><label for="profile-org">所属单位</label><input id="profile-org" v-model.trim="form.org" class="field full" maxlength="100" autocomplete="organization" placeholder="请输入所属单位"></div>
            <div class="form-item"><label for="profile-email">电子邮箱</label><input id="profile-email" v-model.trim="form.email" class="field full" type="email" maxlength="254" autocomplete="email" placeholder="请输入电子邮箱"></div>
          </div>
          <p class="profile-hint">登录账号、角色、学号和班级由管理员维护。</p>
          <div class="profile-actions"><button type="button" class="btn" :disabled="saving" @click="resetProfile">重置</button><button type="submit" class="btn primary" :disabled="saving">{{ saving ? '保存中…' : '保存资料' }}</button></div>
        </form>
      </div>

      <div class="panel profile-card profile-password">
        <div class="profile-heading"><div><h2>修改密码</h2><p>修改成功后，请使用新密码重新登录。</p></div></div>
        <div v-if="passwordError" class="notice error" role="alert">{{ passwordError }}</div>
        <form @submit.prevent="savePassword" @input="passwordError = ''">
          <div class="profile-password-fields">
            <div class="form-item"><label for="profile-current-password">原密码 <span class="profile-required">*</span></label><input id="profile-current-password" v-model="password.currentPassword" class="field full" type="password" required autocomplete="current-password"></div>
            <div class="form-item"><label for="profile-new-password">新密码 <span class="profile-required">*</span></label><input id="profile-new-password" v-model="password.newPassword" class="field full" type="password" minlength="8" maxlength="128" required autocomplete="new-password" placeholder="8～128位"></div>
            <div class="form-item"><label for="profile-confirm-password">确认新密码 <span class="profile-required">*</span></label><input id="profile-confirm-password" v-model="password.confirmPassword" class="field full" type="password" minlength="8" maxlength="128" required autocomplete="new-password"></div>
          </div>
          <div class="profile-actions"><button type="submit" class="btn primary" :disabled="changingPassword">{{ changingPassword ? '提交中…' : '修改密码' }}</button></div>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import { api, errorText } from '../services/api'
import '../assets/profile.css'

export default {
  name: 'Profile',
  components: { PageHeader },
  data () { return { form: { name: '', phone: '', org: '', email: '' }, password: { currentPassword: '', newPassword: '', confirmPassword: '' }, saving: false, changingPassword: false, profileError: '', profileSuccess: '', passwordError: '' } },
  computed: {
    user () { return this.$store.state.currentUser || {} },
    roleName () { return this.user.roleName || ({ ADMIN: '管理员', TEACHER: '教师', STUDENT: '学生' }[this.user.role] || '用户') }
  },
  created () { this.resetProfile() },
  methods: {
    resetProfile () {
      this.form = { name: this.user.name || '', phone: this.user.phone || '', org: this.user.org || '', email: this.user.email || '' }
      this.profileError = ''; this.profileSuccess = ''
    },
    async saveProfile () {
      if (this.saving) return
      this.profileError = ''; this.profileSuccess = ''
      if (!this.form.name.trim()) { this.profileError = '姓名不能为空'; return }
      this.saving = true
      try {
        const user = await api.put('/auth/me', this.form)
        this.$store.commit('setAuth', { token: this.$store.state.token, user })
        this.resetProfile(); this.profileSuccess = '个人资料已保存。'
      } catch (error) { this.profileError = errorText(error) } finally { this.saving = false }
    },
    async savePassword () {
      if (this.changingPassword) return
      this.passwordError = ''
      if (this.password.newPassword !== this.password.confirmPassword) { this.passwordError = '两次输入的新密码不一致'; return }
      if (this.password.newPassword === this.password.currentPassword) { this.passwordError = '新密码不能与原密码相同'; return }
      this.changingPassword = true
      try {
        await api.put('/auth/me/password', { currentPassword: this.password.currentPassword, newPassword: this.password.newPassword })
        this.password = { currentPassword: '', newPassword: '', confirmPassword: '' }
        try { await this.$store.dispatch('logout') } catch (_) { /* 退出失败时 store 仍会清除本地会话 */ }
        this.$router.replace({ path: '/login', query: { passwordChanged: '1' } })
      } catch (error) { this.passwordError = errorText(error) } finally { this.changingPassword = false }
    }
  }
}
</script>
